import React from 'react'
import { FormattedMessage } from 'react-intl'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  Cvss3Vectors,
  parseCvssVectors,
  shouldUseCvss3
} from '../../sca/libraries/helpers/cvssUtils'
import { CvssScoringType } from '../../sca/libraries/helpers/ui-interfaces'
import { Vulnerability } from '../shared'

interface Props {
  vulnerability: Vulnerability
  cvssScoringType: CvssScoringType
}

const descriptionRowContainerCss = css`
  display: flex;
  margin-top: 5px;
  width: 100%;
`

const descriptionRowHeaderCss = css`
  color: ${theme.color.neutral80};
  font-weight: 700;
  flex-basis: 40%;
`

const VectorChildren = (children: React.ReactNode[]) => (
  <div className={descriptionRowHeaderCss}>{children}</div>
)

const DialogVector = ({
  vectorLabel,
  vectorValue
}: {
  vectorLabel: string
  vectorValue?: string
}) => (
  <>
    {vectorValue && (
      <div className={descriptionRowContainerCss}>
        <FormattedMessage id={vectorLabel} children={VectorChildren} />
        <div>
          <span>{vectorValue}</span>
        </div>
      </div>
    )}
  </>
)

export const VulnerabilityModalVectors = ({
  vulnerability,
  cvssScoringType
}: Props) => {
  const {
    confidentialityImpact,
    integrityImpact,
    availabilityImpact,
    authentication,
    accessVector,
    accessComplexity,
    cvss3Vector
  } = vulnerability

  let cvss3Vectors: Cvss3Vectors | undefined

  if (shouldUseCvss3(vulnerability, cvssScoringType) && cvss3Vector) {
    cvss3Vectors = parseCvssVectors(cvss3Vector, CvssScoringType.CVSS3_1)
  }

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        margin-top: 15px;
      `}
    >
      <DialogVector
        vectorValue={
          shouldUseCvss3(vulnerability, cvssScoringType)
            ? cvss3Vectors?.confidentialityImpact?.value
            : confidentialityImpact
        }
        vectorLabel="title_cveInfoConfidentialityImpact"
      />

      <DialogVector
        vectorValue={
          shouldUseCvss3(vulnerability, cvssScoringType)
            ? cvss3Vectors?.integrityImpact?.value
            : integrityImpact
        }
        vectorLabel="title_cveInfoIntegrityImpact"
      />

      <DialogVector
        vectorValue={
          shouldUseCvss3(vulnerability, cvssScoringType)
            ? cvss3Vectors?.availabilityImpact?.value
            : availabilityImpact
        }
        vectorLabel="title_cveInfoAvailabilityImpact"
      />

      <DialogVector
        vectorValue={
          !shouldUseCvss3(vulnerability, cvssScoringType)
            ? authentication
            : undefined
        }
        vectorLabel="title_cveInfoAuthenticationRequired"
      />

      <DialogVector
        vectorValue={
          shouldUseCvss3(vulnerability, cvssScoringType)
            ? cvss3Vectors?.attackVector?.value
            : accessVector
        }
        vectorLabel={
          shouldUseCvss3(vulnerability, cvssScoringType)
            ? 'title_cveInfoAttackVector'
            : 'title_cveInfoAccessVector'
        }
      />

      <DialogVector
        vectorValue={
          shouldUseCvss3(vulnerability, cvssScoringType)
            ? cvss3Vectors?.attackComplexity?.value
            : accessComplexity
        }
        vectorLabel={
          shouldUseCvss3(vulnerability, cvssScoringType)
            ? 'title_cveInfoAttackComplexity'
            : 'title_cveInfoAccessComplexity'
        }
      />

      <DialogVector
        vectorValue={
          shouldUseCvss3(vulnerability, cvssScoringType)
            ? cvss3Vectors?.scope?.value
            : undefined
        }
        vectorLabel="title_cveInfoScope"
      />

      <DialogVector
        vectorValue={
          shouldUseCvss3(vulnerability, cvssScoringType)
            ? cvss3Vectors?.userInteraction?.value
            : undefined
        }
        vectorLabel="title_cveInfoUserInteraction"
      />

      <DialogVector
        vectorValue={
          shouldUseCvss3(vulnerability, cvssScoringType)
            ? cvss3Vectors?.privilegesRequired?.value
            : undefined
        }
        vectorLabel="title_cveInfoPrivilegesRequired"
      />
    </div>
  )
}
